/**
 * HxAvatar Component
 *
 * Displays user/entity representations through initials, profile images, or icons. Implements Moon Design System
 * specifications with automatic fallback handling.
 *
 * @since 2025-12-03
 * @example
 *   Basic usage with initials
 *   ```html
 *   <hx-avatar initials="JD" size="md" variant="fill" />
 *   ```
 *
 * @example
 *   With profile image and fallback
 *   ```html
 *   <hx-avatar
 *   [src]="user.avatarUrl"
 *   [alt]="user.name"
 *   [initials]="user.initials"
 *   size="lg" />
 *   ```
 *
 * @example
 *   Custom icon via content projection
 *   ```html
 *   <hx-avatar size="xl">
 *   <hx-icon svgIcon="user" hxAvatarIcon />
 *   </hx-avatar>
 *   ```
 *
 * @package @complexcare/harmony
 */

import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal, ViewEncapsulation } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxControlSize, HxControlVariant } from '../../base/types';
import { HxIcon } from '../icon/icon';
import { getAvatarColor, getNameInitials, type AvatarColorPair } from './avatar-colors';

/** Avatar size variants matching Moon Design System specifications */
export type AvatarSize = Extract<HxControlSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'>;

/** Avatar visual variants for background and border contrast */
export type AvatarVariant = Extract<HxControlVariant, 'fill' | 'soft' | 'outline'>;

/** Content resolution priority for avatar display */
type AvatarContentType = 'image' | 'initials' | 'icon';

@Component({
  selector: 'hx-avatar',
  standalone: true,
  imports: [CommonModule, A11yModule, HxIcon],
  templateUrl: './avatar.html',
  styleUrl: './avatar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    role: 'img',
    '[class]': 'hostClasses()',
    '[attr.aria-label]': 'computedAriaLabel()',
    '[style.background-color]': 'coloredBackground()',
    '[style.color]': 'coloredText()',
    '[style.border]': 'coloredBorder()',
  },
})
export class HxAvatar extends HxBaseControl {
  // ==========================================================================
  // Input Signals
  // ==========================================================================

  /**
   * Size variant determining avatar dimensions
   *
   * @default 'md'
   */
  readonly size = input<AvatarSize>('md');

  /**
   * Visual variant for background and border styling
   *
   * @default 'fill'
   */
  readonly variant = input<AvatarVariant>('fill');

  /** Image URL for profile picture Falls back to initials or icon on load failure */
  readonly src = input<string | undefined>(undefined);

  /**
   * Alt text for image accessibility Required when src is provided (WCAG 2.1 AA)
   *
   * @default ''
   */
  readonly alt = input<string>('');

  /**
   * Initials text to display Automatically truncated to 2 characters using first-and-last strategy Examples: "ABCD" →
   * "AD", "JD" → "JD", "J" → "J"
   */
  readonly initials = input<string | undefined>(undefined);

  /**
   * Icon name for custom icon display References Harmony icon system
   *
   * @default 'user'
   */
  readonly icon = input<string>('user');

  /**
   * Enables deterministic color assignment based on the `name` input. When true, the avatar background and text color
   * are computed from the name hash.
   *
   * @default false
   */
  readonly colored = input<boolean>(false);

  /**
   * Full name used for colored avatar hashing and auto-generating initials. When provided, initials are automatically
   * derived (first char of first + last name). The `initials` input takes priority over auto-generated initials from
   * `name`.
   */
  readonly name = input<string | undefined>(undefined);

  // ==========================================================================
  // Protected State
  // ==========================================================================

  /**
   * Image loading error state Set to true when image fails to load, triggers fallback
   *
   * @internal
   */
  protected readonly imageLoadError = signal<boolean>(false);

  // ==========================================================================
  // Computed Signals
  // ==========================================================================

  /**
   * Truncated initials following first-and-last character strategy
   *
   * @remarks
   *   - 1 char: Preserved (e.g., "J" → "J")
   *   - 2 chars: Preserved (e.g., "JD" → "JD")
   *   - > 2 chars: First + last (e.g., "ABCD" → "AD", "John" → "JN")
   *   - Always uppercase for consistency
   *
   * @internal
   */
  /**
   * Resolved color pair when colored mode is active. Returns the deterministic color based on `name` input.
   *
   * @internal
   */
  readonly resolvedColor = computed<AvatarColorPair | null>(() => {
    if (!this.colored()) {
      return null;
    }
    // Use name for color hashing; fall back to initials if name is not provided
    return getAvatarColor(this.name() ?? this.initials());
  });

  protected readonly truncatedInitials = computed(() => {
    // Auto-generate initials from name if explicit initials not provided
    const initials = this.initials() || (this.name() ? getNameInitials(this.name()) : undefined);
    if (!initials) {
      return '';
    }

    const trimmed = initials.trim();
    if (trimmed.length === 0) {
      return '';
    }
    if (trimmed.length === 1) {
      return (trimmed.charAt(0) || '').toUpperCase();
    }
    if (trimmed.length === 2) {
      return trimmed.toUpperCase();
    }

    // >2 chars: first + last character
    return (trimmed.charAt(0) + trimmed.charAt(trimmed.length - 1)).toUpperCase();
  });

  /**
   * Dynamic CSS classes for host element
   *
   * @remarks
   *   Combines base, size, and variant classes Example output: "hx-avatar hx-avatar-md hx-avatar-fill"
   * @internal
   */
  protected readonly hostClasses = computed(() => {
    const size = this.size();
    const variant = this.variant();
    const colored = this.colored() ? ' hx-avatar-colored' : '';

    return `hx-avatar hx-avatar-${size} hx-avatar-${variant}${colored}`;
  });

  /** Background color for host style binding when colored mode is active */
  protected readonly coloredBackground = computed(() => {
    return this.resolvedColor()?.bg ?? null;
  });

  /** Text color for host style binding when colored mode is active */
  protected readonly coloredText = computed(() => {
    return this.resolvedColor()?.text ?? null;
  });

  /** Border style for host style binding when colored mode is active */
  protected readonly coloredBorder = computed(() => {
    const color = this.resolvedColor();
    return color ? `1px solid ${color.border}` : null;
  });

  /**
   * ARIA label for screen reader accessibility
   *
   * @remarks
   *   Priority: explicit alt text > initials context > generic label
   * @internal
   */
  protected readonly computedAriaLabel = computed(() => {
    // Use base class ariaLabel input if provided, otherwise compute from alt/initials
    const baseAriaLabel = this.ariaLabel();
    if (baseAriaLabel) {
      return baseAriaLabel;
    }

    const alt = this.alt();
    const initials = this.truncatedInitials();

    if (alt) {
      return alt;
    }
    if (initials) {
      return `Avatar for ${initials}`;
    }
    return 'User avatar';
  });

  /**
   * Content type to display based on availability and priority
   *
   * @remarks
   *   Priority chain: Image → Initials → Icon
   *
   *   - Image: If src provided and hasn't failed to load
   *   - Initials: If initials provided (after image failure or no src)
   *   - Icon: Fallback when image and initials unavailable
   *
   * @internal
   */
  protected readonly displayContent = computed<AvatarContentType>(() => {
    if (this.src() && !this.imageLoadError()) {
      return 'image';
    }
    if (this.initials() || this.name()) {
      return 'initials';
    }
    return 'icon';
  });

  /**
   * Icon size based on avatar size variant
   *
   * @remarks
   *   Mapping from Moon Design System specifications:
   *
   *   - Xs: 12px
   *   - Sm: 16px
   *   - Md/lg/xl/2xl: 20px
   *
   * @internal
   */
  protected readonly iconSize = computed(() => {
    const size = this.size();
    const sizeMap: Record<AvatarSize, number> = {
      xs: 12,
      sm: 16,
      md: 20,
      lg: 20,
      xl: 20,
      '2xl': 20,
    };
    return sizeMap[size];
  });

  // ==========================================================================
  // Protected Methods
  // ==========================================================================

  /**
   * Handles image loading errors Sets imageLoadError signal to trigger fallback to initials or icon
   *
   * @internal
   */
  protected onImageError(): void {
    this.imageLoadError.set(true);
  }

  // ==========================================================================
  // Lifecycle Hooks
  // ==========================================================================

  constructor() {
    super();
    // Development-only warning for missing alt text
    if (typeof ngDevMode !== 'undefined' && ngDevMode) {
      effect(() => {
        const src = this.src();
        const alt = this.alt();
        if (src && !alt) {
          console.warn(
            'HxAvatar: Image src provided without alt text. Provide alt text for accessibility (WCAG 2.1 AA).',
            { src }
          );
        }
      });
    }
  }
}
